/**
 * v1.3.21 — Offline Field-Edit Queue (Focus Mode Phase 3b).
 *
 * Companion of `deferredAiJobsQueue.js`: same design, different
 * purpose. Where the AI-jobs queue holds long-running server actions
 * to be re-invoked at reconnect, THIS queue holds simple
 * write-through PATCH bodies for entity fields (asset / facility)
 * that the user typed while Focus Mode was ON or the browser was
 * offline. When effectively-online returns, the sync worker
 * (`offlineFieldEditSyncManager.js`) replays each PATCH in FIFO
 * insertion order — so the server sees the user's edits in exactly
 * the order they were made.
 *
 * ────────────────────────────────────────────────────────────────
 * DESIGN INVARIANT (identical to every other Phase 2/3 queue)
 * ────────────────────────────────────────────────────────────────
 *   • Its own IndexedDB (`fmiq_offline_field_edits_db`).
 *   • Its own object store (`pending_edits`).
 *   • Never imports from any OTHER offline module (isolation
 *     contract). Enforced by v1.3.21 tripwire.
 *   • Never talks to the network — that's the sync worker's job.
 *   • Deleting this file breaks ONLY the "queue simple edits"
 *     feature; all other offline flows keep working.
 *
 * ────────────────────────────────────────────────────────────────
 * RECORD SHAPE
 * ────────────────────────────────────────────────────────────────
 *   {
 *     id:          <autoincrement>,
 *     edit_id:     <client UUID for cross-tab dedup>,
 *     kind:        'asset' | 'facility',
 *     entity_id:   <string id of the asset or facility being patched>,
 *     endpoint:    <string — full path with leading slash, resolved
 *                    against REACT_APP_BACKEND_URL at drain time>,
 *     body:        <object — the PATCH payload>,
 *     label:       <human-readable line for the drawer, e.g.
 *                    "Edit Facility • Denver Warehouse (name, sq_ft)">,
 *     queued_at:   <ISO string — drain order is ASC on this field>,
 *     status:      'pending' | 'running' | 'completed' | 'failed',
 *     retry_count: <int>,
 *     last_error:  <string | null>,
 *   }
 */

const DB_NAME = 'fmiq_offline_field_edits_db';
const DB_VERSION = 1;
const STORE = 'pending_edits';

let dbInstance = null;
let dbFailed = false;

const _openDB = () =>
  new Promise((resolve, reject) => {
    if (dbFailed) return reject(new Error('offline-field-edits DB failed earlier'));
    if (dbInstance) return resolve(dbInstance);
    if (!window.indexedDB) {
      dbFailed = true;
      return reject(new Error('IndexedDB not available'));
    }
    const req = window.indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (ev) => {
      const db = ev.target.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const s = db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
        s.createIndex('edit_id', 'edit_id', { unique: false });
        s.createIndex('status', 'status', { unique: false });
        s.createIndex('kind', 'kind', { unique: false });
        s.createIndex('entity_id', 'entity_id', { unique: false });
      }
    };
    req.onsuccess = () => {
      dbInstance = req.result;
      resolve(dbInstance);
    };
    req.onerror = () => {
      dbFailed = true;
      reject(req.error);
    };
  });

const _uuid = () =>
  window.crypto?.randomUUID
    ? window.crypto.randomUUID()
    : 'eid-' + Math.random().toString(36).slice(2) + Date.now().toString(36);

// ─── Public API ─────────────────────────────────────────────────────

/**
 * Enqueue a PATCH edit.
 *
 * Callers construct the URL path (with leading slash) and the body
 * exactly as they would for a live `axios.patch(...)` call — the
 * sync worker resolves the full URL against
 * `${REACT_APP_BACKEND_URL}/api` at drain time.
 */
export const enqueueFieldEdit = async ({ kind, entity_id, endpoint, body, label = '' }) => {
  if (!kind || !endpoint) throw new Error('enqueueFieldEdit: kind and endpoint are required');
  const db = await _openDB();
  const record = {
    edit_id: _uuid(),
    kind,
    entity_id: entity_id || null,
    endpoint,
    body: body || {},
    label: label || `${kind} edit`,
    queued_at: new Date().toISOString(),
    status: 'pending',
    retry_count: 0,
    last_error: null,
  };
  const stored = await new Promise((resolve, reject) => {
    const tx = db.transaction([STORE], 'readwrite');
    const req = tx.objectStore(STORE).add(record);
    req.onsuccess = () => resolve({ ...record, id: req.result });
    req.onerror = () => reject(req.error);
  });
  publishFieldEditsChanged();
  return stored;
};

/** Return all queued edits, sorted by queued_at ASC (FIFO drain order). */
export const listFieldEdits = async () => {
  try {
    const db = await _openDB();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction([STORE], 'readonly');
      const req = tx.objectStore(STORE).getAll();
      req.onsuccess = () => {
        const rows = req.result || [];
        // FIFO — insertion order (per user's Phase 3b preference).
        rows.sort((a, b) => (a.queued_at || '').localeCompare(b.queued_at || ''));
        resolve(rows);
      };
      req.onerror = () => reject(req.error);
    });
  } catch (_) {
    return [];
  }
};

/** Only pending or running edits (drain worker's input). */
export const listPendingFieldEdits = async () => {
  const all = await listFieldEdits();
  return all.filter((e) => e.status === 'pending' || e.status === 'running');
};

/** Count of pending edits — used by the Focus Mode drawer badge. */
export const getPendingFieldEditsCount = async () => {
  const rows = await listPendingFieldEdits();
  return rows.length;
};

/** Remove one edit by autoincrement id. */
export const removeFieldEdit = async (id) => {
  const db = await _openDB();
  await new Promise((resolve, reject) => {
    const tx = db.transaction([STORE], 'readwrite');
    const req = tx.objectStore(STORE).delete(id);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
  publishFieldEditsChanged();
};

/** Update status (+ optional last_error). Bumps retry_count when moving to running. */
export const updateFieldEditStatus = async (id, status, last_error = null) => {
  const db = await _openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction([STORE], 'readwrite');
    const store = tx.objectStore(STORE);
    const getReq = store.get(id);
    getReq.onsuccess = () => {
      const row = getReq.result;
      if (!row) return resolve();
      row.status = status;
      if (last_error != null) row.last_error = last_error;
      if (status === 'running') row.retry_count = (row.retry_count || 0) + 1;
      const putReq = store.put(row);
      putReq.onsuccess = () => resolve(row);
      putReq.onerror = () => reject(putReq.error);
    };
    getReq.onerror = () => reject(getReq.error);
  });
};

/** Housekeeping — remove every completed row. */
export const clearCompletedFieldEdits = async () => {
  const rows = await listFieldEdits();
  const done = rows.filter((r) => r.status === 'completed');
  for (const r of done) {
    // eslint-disable-next-line no-await-in-loop
    await removeFieldEdit(r.id);
  }
  return done.length;
};

// ─── Pub-sub for the queue drawer ────────────────────────────────────
const _listeners = new Set();
export const subscribeFieldEdits = (cb) => {
  _listeners.add(cb);
  return () => { _listeners.delete(cb); };
};
export const publishFieldEditsChanged = () => {
  _listeners.forEach((cb) => { try { cb(); } catch (_) { /* isolated */ } });
};
